import type { DrvPrimaryType } from '@/lib/api/drv';

const normalizeNutrientId = (value: string | null | undefined): string =>
  (value || '').replace(/[_\s-]/g, '').toLowerCase();

const normalizePrimaryType = (value: string | null | undefined): string =>
  (value || '').trim().toUpperCase();

const toFinitePositiveNumber = (value: unknown): number | undefined => {
  const parsed = typeof value === 'string' ? Number(value) : value;
  return typeof parsed === 'number' && Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
};

function flattenDrvRequirementRows(drvRoot: Record<string, unknown> | null): unknown[] {
  if (!drvRoot) return [];
  const macros = drvRoot.macronutrients as Record<string, unknown[]> | undefined;
  const micros = drvRoot.micronutrients as Record<string, unknown[]> | undefined;
  return [
    ...(macros?.carbohydrates ?? []),
    ...(macros?.proteins ?? []),
    ...(macros?.lipids ?? []),
    ...(micros?.mineralsMajor ?? []),
    ...(micros?.mineralsTrace ?? []),
    ...(micros?.vitaminsFatSoluble ?? []),
    ...(micros?.vitaminsWaterSoluble ?? []),
  ];
}

function unwrapDrvRequirements(drvRequirements: unknown): Record<string, unknown> | null {
  if (!drvRequirements || typeof drvRequirements !== 'object') return null;
  const r = drvRequirements as Record<string, unknown>;
  const inner = (r.drvRequirements ?? r.data ?? r) as unknown;
  return inner && typeof inner === 'object' ? (inner as Record<string, unknown>) : null;
}

/**
 * Picks RDA / AI / EAR / UL (etc.) from client DRV requirements using `/drv/primary-types`
 * mapping — same rules as `NutritionReportTable` suggested RDA / client analysis.
 */
export function resolveDrvRecommendedValue(
  drvRequirements: unknown,
  primaryTypes: DrvPrimaryType[] | undefined,
  nutrientIdCandidates: string[],
  fallback: number,
): number {
  const reqs = unwrapDrvRequirements(drvRequirements);
  if (!reqs) return fallback;

  const allRequirements = flattenDrvRequirementRows(reqs);

  const primaryTypeByNutrient = new Map<string, DrvPrimaryType>();
  if (primaryTypes) {
    primaryTypes.forEach((item) => {
      primaryTypeByNutrient.set(normalizeNutrientId(item.nutrientId), item);
    });
  }

  const requirement = allRequirements.find((row) => {
    const r = row as { nutrientId?: unknown };
    const nid = normalizeNutrientId(String(r.nutrientId ?? ''));
    return nutrientIdCandidates.some((c) => normalizeNutrientId(c) === nid);
  }) as Record<string, unknown> | undefined;

  if (!requirement) return fallback;

  const rdaValue = toFinitePositiveNumber(requirement.rda);
  if (rdaValue !== undefined) return rdaValue;

  const isTotalFatNutrient = nutrientIdCandidates.some((candidate) => {
    const normalized = normalizeNutrientId(candidate);
    return normalized === 'totalfat' || normalized === 'fat' || normalized === 'lipid';
  });

  if (isTotalFatNutrient) {
    const totalFatUlGrams = toFinitePositiveNumber(requirement.ulGrams);
    if (totalFatUlGrams !== undefined) {
      return totalFatUlGrams;
    }
  }

  const primaryTypeEntry = nutrientIdCandidates
    .map((candidate) => primaryTypeByNutrient.get(normalizeNutrientId(candidate)))
    .find((entry): entry is DrvPrimaryType => Boolean(entry));

  const primaryType = normalizePrimaryType(primaryTypeEntry?.primaryType || primaryTypeEntry?.primaryRawType);

  if (primaryType === 'ALAP') {
    return fallback;
  }

  let primaryField = primaryTypeEntry?.primaryField;
  if (!primaryField) {
    const pType = primaryType.toLowerCase();
    if (pType === 'rda') primaryField = 'rda';
    else if (pType === 'ai') primaryField = 'ai';
    else if (pType === 'ear') primaryField = 'ear';
    else if (pType === 'lrni') primaryField = 'lrni';
    else if (pType.includes('amdr')) primaryField = 'amdr';
  }

  if (primaryField && !String(primaryField).toLowerCase().includes('amdr')) {
    if (isTotalFatNutrient && String(primaryField).toLowerCase() === 'ul') {
      // Skip percent-based UL for total fat; use grams fallback logic instead.
    } else {
      const directValue = toFinitePositiveNumber((requirement as Record<string, unknown>)[primaryField]);
      if (directValue !== undefined) {
        return directValue;
      }
    }
  }

  const fallbackVal =
    toFinitePositiveNumber(requirement.amdr) ??
    toFinitePositiveNumber(requirement.ai) ??
    toFinitePositiveNumber(requirement.ear) ??
    (isTotalFatNutrient ? undefined : toFinitePositiveNumber(requirement.ul));

  return fallbackVal ?? fallback;
}

/**
 * Strict resolver that only uses `/drv/primary-types` selected field.
 * Does not fallback to generic RDA/AI/EAR/UL when primary field is missing.
 */
export function resolveDrvPrimaryTypeValueOnly(
  drvRequirements: unknown,
  primaryTypes: DrvPrimaryType[] | undefined,
  nutrientIdCandidates: string[],
  fallback: number,
): number {
  const reqs = unwrapDrvRequirements(drvRequirements);
  if (!reqs) return fallback;

  const allRequirements = flattenDrvRequirementRows(reqs);
  const requirement = allRequirements.find((row) => {
    const r = row as { nutrientId?: unknown };
    const nid = normalizeNutrientId(String(r.nutrientId ?? ''));
    return nutrientIdCandidates.some((c) => normalizeNutrientId(c) === nid);
  }) as Record<string, unknown> | undefined;
  if (!requirement) return fallback;

  const primaryTypeByNutrient = new Map<string, DrvPrimaryType>();
  if (primaryTypes) {
    primaryTypes.forEach((item) => {
      primaryTypeByNutrient.set(normalizeNutrientId(item.nutrientId), item);
    });
  }

  const primaryTypeEntry = nutrientIdCandidates
    .map((candidate) => primaryTypeByNutrient.get(normalizeNutrientId(candidate)))
    .find((entry): entry is DrvPrimaryType => Boolean(entry));
  if (!primaryTypeEntry) return fallback;

  let primaryField = primaryTypeEntry.primaryField;
  if (!primaryField) {
    const primaryType = normalizePrimaryType(primaryTypeEntry.primaryType || primaryTypeEntry.primaryRawType).toLowerCase();
    if (primaryType === 'rda') primaryField = 'rda';
    else if (primaryType === 'ai') primaryField = 'ai';
    else if (primaryType === 'ear') primaryField = 'ear';
    else if (primaryType === 'lrni') primaryField = 'lrni';
    else if (primaryType === 'ul') primaryField = 'ul';
  }
  if (!primaryField || String(primaryField).toLowerCase().includes('amdr')) return fallback;

  return toFinitePositiveNumber((requirement as Record<string, unknown>)[primaryField]) ?? fallback;
}

/** First DRV row matching any of `nutrientIdCandidates` (e.g. `carbohydrate` / `total_fat`). */
export function findDrvRequirementByNutrientIds(
  drvRequirements: unknown,
  nutrientIdCandidates: string[],
): Record<string, unknown> | undefined {
  const reqs = unwrapDrvRequirements(drvRequirements);
  if (!reqs) return undefined;
  const allRequirements = flattenDrvRequirementRows(reqs);
  return allRequirements.find((row) => {
    const r = row as { nutrientId?: unknown };
    const nid = normalizeNutrientId(String(r.nutrientId ?? ''));
    return nutrientIdCandidates.some((c) => normalizeNutrientId(c) === nid);
  }) as Record<string, unknown> | undefined;
}
